import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { CreditCard, Shield, ArrowLeft, Clock } from 'lucide-react';
import { OrbitIcon } from "@/components/icons/OrbitIcon";
import { useToast } from '@/hooks/use-toast';
import {
  PLAN_PRICING,
  PLAN_FEATURES,
  PLAN_LABELS,
  STRIPE_IDS,
  isPlaceholderPriceId,
  type SubscriptionPlan,
  readFunctionErrorBody,
} from '@/services/billing';
import { NovaSpinner } from '@/components/nova';
// P0-1 — the last gate before Stripe. Everything above this component
// hides Orbit Business while it's held, but this is the one place that
// actually calls `create-checkout`, so the refusal lives here too.
import { BUSINESS_ON_SALE, BUSINESS_HELD_MESSAGE } from '@/config/launch';

interface EmbeddedCheckoutProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  plan: 'orbit_starter' | 'orbit' | 'orbit_business';
  interval?: 'monthly' | 'yearly';
  onSuccess?: () => void;
}

// CHECKOUT-2 — what create-checkout answers to `preview: true` for a
// studio that already pays: the prorated amount today and what the
// plan costs from the next renewal. Rendered as a review step so a
// plan change is confirmed against the real number, never charged
// from a button that said "Continue to Payment".
interface PlanChangePreview {
  preview: true;
  plan: SubscriptionPlan;
  interval: 'monthly' | 'yearly';
  currency: string;
  amountDueCents: number;
  startingBalanceCents: number;
  nextAmountCents: number | null;
  nextRenewalAt: number | null;
  trialing: boolean;
}

const money = (cents: number, currency: string) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(cents / 100);

export default function EmbeddedCheckout({
  open,
  onOpenChange,
  plan,
  interval = 'monthly',
  onSuccess
}: EmbeddedCheckoutProps) {
  const [loading, setLoading] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
  // CHECKOUT-2 — null until we know whether this is a plan CHANGE
  // (live subscription) or a first subscription. The primary button
  // is held while that is unknown so nothing can be charged on a
  // click that landed before the answer.
  const [preview, setPreview] = useState<PlanChangePreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const { toast } = useToast();

  const pricing = PLAN_PRICING[plan];
  const features = PLAN_FEATURES[plan];
  const price = interval === 'yearly' && pricing.yearly ? pricing.yearly : pricing.monthly;
  // PLANS-1 — canonical label rather than title-casing the plan code
  // (so "orbit_starter" → "Orbit Starter", not "Orbit_starter").
  const planName = PLAN_LABELS[plan];
  const queryClient = useQueryClient();

  // P0-1 — is this plan actually buyable right now?
  //
  // Two independent reasons it might not be, and we check both so a
  // future price-ID slip can't reintroduce the same bug:
  //
  //   1. The launch decision — Orbit Business goes on sale with video
  //      in mid-September (`BUSINESS_ON_SALE`).
  //   2. The Stripe reality — PLANS-5 replaced Business's price IDs
  //      with `price_REPLACE_ME_*`. `isPlaceholderPriceId` has been
  //      exported from billing.ts since PLANS-5 with zero importers;
  //      this is its first consumer. `create-checkout` answers 503
  //      `price_not_configured` for these, and until now that failure
  //      only ever appeared AFTER the studio clicked "Continue to
  //      Payment" — which is the whole bug.
  const priceId =
    interval === 'yearly'
      ? STRIPE_IDS[plan].price_yearly
      : STRIPE_IDS[plan].price_monthly;
  const notPurchasable =
    (plan === 'orbit_business' && !BUSINESS_ON_SALE) ||
    isPlaceholderPriceId(priceId);

  useEffect(() => {
    if (!open || notPurchasable) {
      setPreview(null);
      setPreviewing(false);
      return;
    }
    let cancelled = false;
    setPreviewing(true);
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke('create-checkout', {
          body: { plan, interval, preview: true },
        });
        if (cancelled) return;
        if (error) {
          const body = await readFunctionErrorBody(error);
          if (body?.code === 'already_on_plan') {
            toast({ title: "You're already on this plan" });
            onOpenChange(false);
            return;
          }
          // Any other failure: fall through to the normal checkout
          // path; create-checkout will say what's wrong on the click.
          setPreview(null);
          return;
        }
        const payload = data as PlanChangePreview | { preview: false } | null;
        setPreview(payload && payload.preview ? payload : null);
      } finally {
        if (!cancelled) setPreviewing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, plan, interval, notPurchasable]);

  const handleStartCheckout = async () => {
    if (notPurchasable) {
      // Not reachable from the UI (the CTA below is swapped out when
      // `notPurchasable`), but we never hand Stripe a plan we already
      // know it will reject.
      toast({
        title: "This plan isn't on sale yet",
        description: BUSINESS_HELD_MESSAGE,
      });
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { plan, interval },
      });

      // CHECKOUT-1 — a studio that already pays doesn't get a second
      // checkout; create-checkout swaps the plan on their existing
      // subscription and answers `{ changed: true }`. Nothing to
      // embed: confirm, refresh the entitlement, close.
      const payload = data as
        | { url?: string; changed?: boolean; code?: string; message?: string }
        | null;
      if (payload?.changed) {
        await queryClient.invalidateQueries({ queryKey: ['subscription-status'] });
        await queryClient.invalidateQueries({ queryKey: ['subscription-events'] });
        toast({
          title: `You're on ${planName}`,
          description:
            'Your plan changed on your existing subscription. Any difference is prorated on your next invoice.',
        });
        onOpenChange(false);
        return;
      }
      if (payload?.code === 'already_on_plan') {
        toast({ title: "You're already on this plan" });
        onOpenChange(false);
        return;
      }

      if (error) {
        // supabase-js wraps non-2xx responses; the 409's reason is in
        // the response body, not in error.message.
        const body = await readFunctionErrorBody(error);
        if (body?.code === 'already_on_plan') {
          toast({ title: "You're already on this plan" });
          onOpenChange(false);
          return;
        }
        // Older deploy of create-checkout: it can't change a plan in
        // place yet and answers 409 `subscription_active` instead.
        if (body?.code === 'subscription_active') {
          toast({
            title: 'You already have a subscription',
            description:
              body.message ?? 'Use Manage billing in Settings → Billing to change your plan.',
          });
          onOpenChange(false);
          return;
        }
        throw new Error(body?.message || error.message);
      }
      if (!payload?.url) throw new Error('No checkout URL returned');

      setCheckoutUrl(payload.url);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create checkout session';
      toast({
        title: 'Checkout Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCheckoutUrl(null);
    onOpenChange(false);
  };

  const handleBack = () => {
    setCheckoutUrl(null);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden p-0">
        {checkoutUrl ? (
          <div className="flex flex-col h-[85vh]">
            <div className="flex items-center gap-3 p-4 border-b bg-muted/30">
              <Button variant="ghost" size="icon" onClick={handleBack}>
                <OrbitIcon name="arrow.left" fallback={ArrowLeft} className="h-4 w-4" />
              </Button>
              <div className="flex-1">
                <h3 className="font-editorial text-xl text-foreground leading-none">Subscribe to {planName}</h3>
                <p className="text-sm text-muted-foreground">
                  Complete your payment with Stripe
                </p>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <OrbitIcon name="shield" fallback={Shield} className="h-4 w-4" />
                <span className="text-xs">Secured by Stripe</span>
              </div>
            </div>
            <iframe
              src={checkoutUrl}
              className="flex-1 w-full border-0"
              title="Stripe Checkout"
              allow="payment"
            />
          </div>
        ) : (
          <div className="p-6">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <OrbitIcon name="creditcard" fallback={CreditCard} className="h-5 w-5 text-primary" />
                {preview ? `Switch to ${planName}` : `Subscribe to ${planName}`}
              </DialogTitle>
              <DialogDescription>
                {preview
                  ? 'Review the change before anything is charged.'
                  : interval === 'yearly' && pricing.yearly
                    ? `$${pricing.yearly}/year (save $${(pricing.monthly * 12) - pricing.yearly})`
                    : `$${pricing.monthly}/month`}
              </DialogDescription>
            </DialogHeader>

            <div className="mt-6 space-y-6">
              {/* Features list */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium">What's included:</h4>
                <ul className="space-y-2">
                  {features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <span className="text-primary mt-0.5">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Price breakdown */}
              {preview ? (
                <div className="rounded-lg border bg-muted/30 p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>{planName}</span>
                    <span>
                      {preview.nextAmountCents != null
                        ? `${money(preview.nextAmountCents, preview.currency)}/${preview.interval === 'yearly' ? 'year' : 'month'}`
                        : `$${price}/${interval === 'yearly' ? 'year' : 'month'}`}
                    </span>
                  </div>
                  <div className="border-t pt-2 flex justify-between font-medium">
                    <span>{preview.trialing ? 'Due when your trial ends' : 'Due today (prorated)'}</span>
                    <span>{money(preview.amountDueCents, preview.currency)}</span>
                  </div>
                  {preview.startingBalanceCents < 0 && (
                    <p className="text-xs text-muted-foreground">
                      Your unused time on the current plan comes back as a {money(-preview.startingBalanceCents, preview.currency)} credit on your next invoice.
                    </p>
                  )}
                  {preview.nextRenewalAt && (
                    <p className="text-xs text-muted-foreground">
                      Renews {new Date(preview.nextRenewalAt * 1000).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}.
                    </p>
                  )}
                </div>
              ) : (
                <div className="rounded-lg border bg-muted/30 p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>{planName}</span>
                    <span>${price}/{interval === 'yearly' ? 'year' : 'month'}</span>
                  </div>
                  <div className="border-t pt-2 flex justify-between font-medium">
                    <span>Total due today</span>
                    <span>${price}</span>
                  </div>
                </div>
              )}

              {/* Security badge */}
              <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                <OrbitIcon name="shield" fallback={Shield} className="h-3 w-3" />
                <span>256-bit SSL encryption • Powered by Stripe</span>
              </div>

              {/* P0-1 — honest, unclickable state instead of a live
                  CTA into a 503. */}
              {notPurchasable && (
                <div className="flex items-start gap-3 rounded-lg border border-border/50 bg-muted/30 p-4">
                  <OrbitIcon name="clock" fallback={Clock} className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    {BUSINESS_HELD_MESSAGE} Nothing has been charged.
                  </p>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={handleClose}>
                  {notPurchasable ? 'Close' : 'Cancel'}
                </Button>
                {!notPurchasable && (
                  <Button
                    className="flex-1"
                    onClick={handleStartCheckout}
                    disabled={loading || previewing}
                  >
                    {(loading || previewing) && <NovaSpinner size={16} className="mr-2" />}
                    {previewing ? 'Checking your plan…' : preview ? 'Confirm change' : 'Continue to Payment'}
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
